import { Box, Card, CardContent, Grid, Typography, useTheme } from "@mui/material";
import { Facility, PROGRAM_IPV } from "./types";
import { capitalizeWord } from "./utils";

export type TableItemProps = {
  facility: Facility;
  backgroundColor?: boolean;
};

function TableItem(props: TableItemProps) {
  const {
    facility: {
      name,
      address,
      tel,
      afterHoursTel,
      type,
      county,
      website,
      email,
      city,
      zip,
      programs,
      serviceHours,
      sane247Availability,
      saneProgram,
      note,
      ages,
    },
    backgroundColor,
  } = props;
  const theme = useTheme();

  const hasAfterHoursTel = afterHoursTel.length > 0;
  const trimmedNote = note.trim();
  const hasEmail = email.length > 0;

  const listSyles = {
    fontSize: "0.65rem",
    paddingLeft: "1.5rem",
  }

  const programStrings = Object.values(programs) as string[];

  return (
    <Card
      sx={{
        backgroundColor: backgroundColor ? "#f3f3f3" : "#fcfcfc",
        border: "1px solid lightgrey",
        marginTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(1.5),
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between">
          <Box flexDirection="column" mr={4}>
            <Typography
              sx={{ fontSize: "0.8rem" }}
              color="text.secondary"
              textAlign={"start"}
              flexShrink={0}
            >
              {type}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: "0.8rem" }}
              color="text.secondary"
              textAlign={"right"}
              flexShrink={0}
              whiteSpace="nowrap"
            >
              {county} County
            </Typography>
          </Box>
        </Box>
        <Typography variant="h6" textAlign="start" sx={{ fontSize: "1rem" }}>
          <b>
            {website ? (
              <a href={website} target="_blank" rel="noreferrer">
                {name}
              </a>
            ) : (
              name
            )}
          </b>
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box paddingTop={theme.spacing(1)} display="flex">
              <Box flex={1}>
                {(address.length > 0 || city.length > 0) && (
                  <Typography
                    variant="h6"
                    component="div"
                    textAlign="start"
                    sx={{
                      fontSize: "0.95rem",
                      mb: 1
                    }}
                  >
                  {address.length > 0 ?
                      (
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            [name, address, city, zip].join(" ")
                          )}`}
                        >
                          {address}
                          <br />
                          {city}, TX {zip}
                        </a>
                      ) : (<>{`${city}, TX ${zip}`}</>)
                    }
                  </Typography>
                )}
                {tel.length > 0 && (
                  <Box
                    sx={{
                      pt: 2
                    }}
                  >
                    {hasAfterHoursTel && (
                      <Typography
                        sx={{
                          fontSize: "0.95rem"
                        }}
                        textAlign="start"
                      >
                        24hr Phone: <a href={`tel:+${afterHoursTel}`}>{afterHoursTel}</a>
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontSize: "0.95rem"
                      }}
                      textAlign="start"
                    >
                      {hasAfterHoursTel ? "Alternate Phone:" : "Phone:"} <a href={`tel:+${tel}`}>{tel}</a>
                    </Typography>
                  </Box>
                )}
                {hasEmail && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.95rem"
                      }}
                      textAlign="start"
                    >
                      Email: <a href={`mailto:+${email}`}>{email}</a>
                    </Typography>
                  </Box>
                )}
                {(sane247Availability || serviceHours.length > 0) && (
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      pt: 3
                    }}
                    color="text.secondary"
                    textAlign={"start"}
                    flexShrink={0}
                  >
                    <b>Hours of Service: </b>
                    {sane247Availability ? "SANE available 24/7" : serviceHours}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            {saneProgram && (
              <Box flex={1} pt={theme.spacing(1)}>
                <Typography textAlign="start" mb={1}>
                  <b>SANE Program(s):</b>
                </Typography>
                <ul style={listSyles}>
                  {programStrings.map((d, i) => (
                    <li key={i}>
                       <Typography
                        variant="subtitle2"
                        textAlign="start"
                      >
                        {d === PROGRAM_IPV ? PROGRAM_IPV : d.split(" ").map(capitalizeWord).join(" ")}
                      </Typography>
                    </li>
                  ))}
                </ul>
                {ages && (
                  <Typography
                    sx={{ fontSize: "0.95rem",
                          pt: 1,
                        }}
                    color="text.secondary"
                    textAlign={"start"}
                    flexShrink={0}
                  >
                    <b>Ages Served: </b>
                    {ages}
                  </Typography>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
        {trimmedNote.length > 0 && (
          <Box>
            <Typography
              textAlign="start"
              sx={{
                fontSize: "0.95rem",
                pt: 3,
              }}
              color="text.secondary"
            >
              <b>Note: </b>{trimmedNote}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default TableItem;
